import type { FC, Dispatch, SetStateAction, RefObject } from 'react';
import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import type { FormattedHeadings, TableOfContentsMap, TableOfContents, HeadingRefs } from '@models/Toc';
import { useLocalStorage } from '@client/hooks/use-local-storage';
import { useUrlHashContext } from '@contexts/UrlHash';
import { useHasMounted } from '@isomorphic/hooks/use-has-mounted';
import { getFormattedHeadings } from '../../utils/toc';

type ContextValue = {
  activeTabIndex: number;
  tableOfContents: TableOfContents;
  formattedHeadings: FormattedHeadings;
  setActiveTabName: (tabName: string) => void;
  headingRefs: HeadingRefs;
  setHeadingRefs: Dispatch<SetStateAction<any>>;
  tocListRef: RefObject<HTMLDivElement> | null;
  setTocListRef: Dispatch<SetStateAction<any>>;
};

type TOCProviderProps = {
  tableOfContentsMap: TableOfContentsMap;
  children?: React.ReactNode;
};

export const TOCContext = createContext<ContextValue>({
  activeTabIndex: 0,
  tableOfContents: [],
  formattedHeadings: [],
  setActiveTabName: () => {},
  headingRefs: [],
  setHeadingRefs: () => {},
  tocListRef: null,
  setTocListRef: () => {},
});

const findHeading = (tableOfContents, headingRef) =>
  tableOfContents.find((header) => header.link.slice(1) === headingRef.current.id);

export const useTOCContext = () => useContext(TOCContext);

export const TOCProvider: FC<TOCProviderProps> = ({ children, tableOfContentsMap = {} }) => {
  const [activeTabIndex, setActiveTabIndex] = useLocalStorage('activeTabIndex', 0, 0);
  const [headingRefs, setHeadingRefs] = useState<HeadingRefs>([]);
  const [tocListRef, setTocListRef] = useState(null);
  const hasMounted = useHasMounted();
  const { hash } = useUrlHashContext();
  const decodedHash = `#${decodeURIComponent(hash)}`;

  const setActiveTabName = useCallback(
    (tabName: string) => {
      const tabsList = Object.keys(tableOfContentsMap);
      const tabIndex = tabsList.indexOf(tabName);
      if (tabIndex > -1) {
        setActiveTabIndex(tabIndex);
      }
    },
    [tableOfContentsMap, setActiveTabIndex]
  );

  const contextValue = useMemo(() => {
    const tabsList = Object.keys(tableOfContentsMap);
    const tableOfContents =
      tableOfContentsMap[tabsList[activeTabIndex]] || tableOfContentsMap.main || tableOfContentsMap[tabsList[0]] || [];

    const filteredHeadingsRefs = headingRefs
      .filter((headingRef) => {
        if (!headingRef.current) {
          return false;
        }
        return findHeading(tableOfContents, headingRef);
      })
      .sort(
        (a, b) =>
          tableOfContents.indexOf(findHeading(tableOfContents, a)) -
          tableOfContents.indexOf(findHeading(tableOfContents, b))
      );

    return {
      formattedHeadings: getFormattedHeadings(tableOfContentsMap),
      setActiveTabName,
      tableOfContents,
      activeTabIndex,
      headingRefs: filteredHeadingsRefs,
      setHeadingRefs,
      tocListRef,
      setTocListRef,
    };
  }, [tableOfContentsMap, activeTabIndex, setActiveTabName, headingRefs, tocListRef]);

  useEffect(() => {
    if (!hasMounted) {
      return;
    }

    const tabEntry = Object.entries(tableOfContentsMap)
      .filter(([tabName]) => tabName !== 'main')
      .find(([_tabName, headers]) => headers.some(({ link }) => link === decodedHash));

    if (tabEntry) {
      const [tabName] = tabEntry;
      setActiveTabName(tabName);
    }
  }, [hasMounted, hash, setActiveTabName, tableOfContentsMap]);

  return <TOCContext.Provider value={contextValue}>{children}</TOCContext.Provider>;
};
